/**
 * Advanced-list Member Entity Schemas
 *
 * A Blackbaud advanced list returns rows of arbitrary {name, value} columns
 * chosen by the list author. Our resolver auto-detects a join key per list
 * (a User-ID-type column → UsersOnSchools.platformId; else email →
 * User.email / SchoolUserProfile.email) and tags each row with a match
 * status. Schema defined here for reuse across backend and web.
 *
 * See: docs/plans/2026-05-21-004-feat-blackbaud-lists-resolve-explorer-plan.md
 */

import { z } from 'zod'

export const ListMemberColumnSchema = z.object({
  name: z.string(),
  value: z.string().optional()
})

export type ListMemberColumn = z.infer<typeof ListMemberColumnSchema>

export const ListMemberMatchStatusSchema = z.enum(['MATCHED', 'AMBIGUOUS', 'UNMATCHED_NO_KEY', 'UNMATCHED_NO_USER'])

export type ListMemberMatchStatus = z.infer<typeof ListMemberMatchStatusSchema>

export const ListMemberSchema = z.object({
  columns: z.array(ListMemberColumnSchema),
  matchStatus: ListMemberMatchStatusSchema,
  /** First resolved User.publicId (back-compat single-actor accessor). */
  matchedUserPublicId: z.string().optional(),
  /**
   * All distinct User.publicIds this row fans out to (dual-actor). The resolver
   * ALWAYS emits this (empty for non-MATCHED rows), so it is required-with-default:
   * consumers read `row.matchedUserPublicIds` without optional-chaining, while
   * older cached responses that omit it still parse (default `[]`).
   */
  matchedUserPublicIds: z.array(z.string()).default([])
})

export type ListMember = z.infer<typeof ListMemberSchema>

/**
 * The auto-detected join key for a list. `none` means the list had neither
 * a User-ID-type column nor an email column — every row will be
 * UNMATCHED_NO_KEY and the Explorer should surface this as a first-class
 * empty-match state, not as an error.
 */
export const ListMemberJoinKeySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('platformId'), column: z.string() }),
  z.object({ kind: z.literal('email'), column: z.string() }),
  z.object({ kind: z.literal('none') })
])

export type ListMemberJoinKey = z.infer<typeof ListMemberJoinKeySchema>

export const ListMemberUnmatchedReasonsSchema = z.object({
  noKey: z.number().min(0),
  noUser: z.number().min(0),
  /**
   * Rows whose join value resolved to >1 distinct User (excluded, never guessed).
   * The resolver ALWAYS emits this; required-with-default so consumers don't
   * optional-chain a field the server always sends, while older cached responses
   * (pre-AMBIGUOUS) still parse (default `0`).
   */
  ambiguous: z.number().min(0).default(0)
})

export type ListMemberUnmatchedReasons = z.infer<typeof ListMemberUnmatchedReasonsSchema>
